import React from 'react'
import { Toast } from 'react-hot-toast/headless'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { switchConnectedWalletChain } from '@components/Web3/ConnectWallet/switchConnectedWalletChain'
import { usePrivyConnectWallet } from '@components/Web3/ConnectWallet/usePrivyConnectWallet'
import { mapToErrorMessage } from '@components/Web3/utils'
import { PrivyWalletReady, PrivyWalletReadyProps, useWallets } from '@towns/privy'
import { getSigner } from '@towns/privy/getSigner'
import { Box, Button, ButtonProps, Icon, IconButton, Text } from '@ui'
import { useDevice } from 'hooks/useDevice'
import { useEnvironment } from 'hooks/useEnvironment'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { TSigner, useMyUserId, usePrimaryWallet } from 'use-towns-client'

import { useCombinedAuth } from './useCombinedAuth'

type Props = Omit<PrivyWalletReadyProps, 'chainId'> & {
    withPrimaryWallet?: boolean
    ConnectButton?: React.JSX.Element
}

/**
 * The signer returned from getSigner:
 * - on desktop:
 *   - primary wallet unset: root key signer (use smart account for ops)
 *   - primary wallet set and connected: primary wallet signer (either root key or EOA)
 *   - primary wallet set but not connected: Renders a Connect Wallet button
 * - on mobile:
 *   - always going to return a root key signer - mobile always using smart account for ops
 * @param props
 * @returns
 */

export function WalletReady(props: Props) {
    const { withPrimaryWallet = false } = props
    const { baseChain } = useEnvironment()
    const { isTouch } = useDevice()

    const {
        WaitForPrivy: WaitForPrivyOverride,
        WaitForWallets: WaitForWalletsOverride,
        LoginButton: LoginButtonOverride,
        children,
    } = props

    if (!isTouch && withPrimaryWallet) {
        return <WalletReadyWithPrimaryWallet {...props} />
    }

    return (
        <PrivyWalletReady
            chainId={baseChain.id}
            WaitForPrivy={WaitForPrivyOverride ?? <SpinnerFallback />}
            WaitForWallets={WaitForWalletsOverride ?? <SpinnerFallback />}
            LoginButton={LoginButtonOverride ?? <ReauthenticateButton />}
        >
            {children}
        </PrivyWalletReady>
    )
}

function WalletReadyWithPrimaryWallet(props: Props) {
    const { baseChain } = useEnvironment()
    const { wallets } = useWallets()
    const {
        WaitForPrivy: WaitForPrivyOverride,
        WaitForWallets: WaitForWalletsOverride,
        LoginButton: LoginButtonOverride,
        children,
        ConnectButton = <PrimaryWalletNotConnectedButton />,
    } = props
    const userId = useMyUserId()
    const { data: primaryWallet } = usePrimaryWallet({ rootWallet: userId })
    if (!primaryWallet) {
        return <SpinnerFallback />
    }

    if (primaryWallet.type === 'smartAccount') {
        return (
            <PrivyWalletReady
                chainId={baseChain.id}
                WaitForPrivy={WaitForPrivyOverride ?? <SpinnerFallback />}
                WaitForWallets={WaitForWalletsOverride ?? <SpinnerFallback />}
                LoginButton={LoginButtonOverride ?? <ReauthenticateButton />}
            >
                {children}
            </PrivyWalletReady>
        )
    }

    const connectedPrimaryWallet = wallets.find(
        (wallet) => wallet.address.toLowerCase() === primaryWallet?.address?.toLowerCase(),
    )

    if (connectedPrimaryWallet) {
        return <>{children({ getSigner: () => getSigner(connectedPrimaryWallet, baseChain.id) })}</>
    }

    return <>{ConnectButton}</>
}

function SpinnerFallback() {
    return (
        <Box horizontal centerContent gap="sm" background="level2" padding="md" rounded="sm">
            <ButtonSpinner />
        </Box>
    )
}

export function ReauthenticateButton(props: { message?: string }) {
    const { privyLogin } = useCombinedAuth()
    const message =
        props.message ?? 'For your security, please click to re-authenticate with Privy.'
    return (
        <Box tooltip={message} width="100%">
            <Button tone="negativeSubtle" onClick={privyLogin}>
                <Icon type="alert" size="square_inline" />
                <Text>Reauthenticate</Text>
            </Button>
        </Box>
    )
}

export type GetSigner = () => Promise<TSigner | undefined>

export function LoginToPrivyIconButton(props: { message?: string }) {
    const { privyLogin } = useCombinedAuth()
    return (
        <IconButton
            icon="alert"
            tooltip={
                props.message ??
                'For your security, performing this action requires you to re-authenticate with Privy.'
            }
            onClick={privyLogin}
        />
    )
}

export function ReauthenticateToast(props: { toast: Toast; message: string; cta: string }) {
    const { privyLogin } = useCombinedAuth()

    return (
        <StandardToast.Error
            toast={props.toast}
            message={props.message}
            cta={props.cta}
            onCtaClick={() => {
                privyLogin()
            }}
        />
    )
}

export function PrimaryWalletNotConnectedButton(props: {
    buttonSize?: ButtonProps['size']
    buttonRounded?: ButtonProps['rounded']
    withTooltip?: boolean
}) {
    const { buttonSize = 'button_md', buttonRounded = 'md', withTooltip = true } = props
    const { baseChain } = useEnvironment()

    const connectWallet = usePrivyConnectWallet({
        onSuccess: async ({ wallet }) => {
            switchConnectedWalletChain({
                wallet,
                baseChain,
                onSuccess: (signer) => {},
            })
        },
        onError: (error) => {
            console.error('[useConnectThenLink] error connecting wallet', error)
            const errorMessage = mapToErrorMessage({
                error: new Error(error),
                source: 'PrimaryWalletNotConnected privy connect error',
            })

            if (errorMessage === 'ACTION_REJECTED') {
                return
            }

            popupToast(({ toast }) => (
                <StandardToast.Error
                    toast={toast}
                    message={`Please make sure your wallet supports and is connected to the ${baseChain.name} network.`}
                    subMessage={errorMessage}
                />
            ))
        },
    })
    return (
        <Box tooltip={withTooltip ? 'Connect primary wallet to proceed.' : undefined} width="100%">
            <Button
                tone="negativeSubtle"
                size={buttonSize}
                rounded={buttonRounded}
                onClick={connectWallet}
            >
                <Icon type="alert" size="square_inline" />
                <Text>Connect Wallet</Text>
            </Button>
        </Box>
    )
}
